<template>
  <div class="global-filters" >
    <el-form label-position="top" :inline="true" class="filters" >
      <el-form-item v-for="(f, i) in filters" v-show="isVisible(f)" :key="i" >
        <component
           :is="f.options.type + '-filter'"
           ref="filter"
           :class="f.options.type + '-filter'"
           :data="filters[i]"
           :placeholder="api.dimension.name(f.value)"
           @change="updateFilters" />
      </el-form-item>
      <el-form-item>
        <el-tag class="merged-graph-filter"
                v-for="f in mergedGraphFilters"
                :key="f.filter"
                :closable="true"
                @close="removeGraphFilter(f)" >
          {{ f.filterName }}
        </el-tag>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="reset" icon="el-icon-fa-eraser" size="small"/>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import EventBus from './events'
import {api} from 'macarons'
import MultipleCascader from './MultipleCascader.vue'
import * as filters from './filter'
import Vue from 'vue'
import Editable from './Editable'
import GlobalFiltersWidgetEdit from './GlobalFiltersWidgetEdit'
import customGraphApiAware from './mixins/customGraphApiAware'

export default {
  props: {
    widget: {
      type: Object,
      required: true
    }
  },
  extends: Editable,
  mixins: [
    customGraphApiAware
  ],
  data () {
    return {
      filters: [],
      graphFilters: [],
      editable: GlobalFiltersWidgetEdit
    }
  },
  mounted () {
    this.load()
    this.$emit('titleChange', 'Filtres')
  },
  created () {
    EventBus.$on('add-filter', this.handleGlobalFilterAdd)
    EventBus.$on('remove-filter', this.handleGlobalFilterRemove)
    EventBus.$on('graph:edit', this.doSetMenu)
    EventBus.$on('graph:cancel', this.doSetMenu)
  },
  beforeDestroy () {
    EventBus.$off('add-filter', this.handleGlobalFilterAdd)
    EventBus.$off('remove-filter', this.handleGlobalFilterRemove)
    EventBus.$on('graph:edit', this.doSetMenu)
    EventBus.$off('graph:cancel', this.doSetMenu)
  },
  computed: {
    mergedGraphFilters () {
      const filters = []
      this.graphFilters.forEach(filter => {
        if (filters.findIndex(f => f.filter === filter.filter) === -1) {
          filters.push({
            filter: filter.filter,
            filterName: filter.filterName,
            data: filter.data,
            source: 'global'
          })
        }
      })
      return filters
    }
  },
  methods: {
    isVisible (filter) {
      if (filter.state === 'hidden') {
        return false
      }
      return filter.state === 'editable' || this.canEdit
    },
    removeGraphFilter (filter) {
      this.graphFilters = this.graphFilters.filter(f => f.filter !== filter.filter)
      EventBus.$emit('remove-filter', filter)
    },
    handleGlobalFilterAdd (filter) {
      if (filter.source !== 'global') {
        if (this.graphFilters.findIndex(
          f => f.filter === filter.filter && f.source === filter.source
        ) === -1) {
          this.graphFilters.push(filter)
        }
      }
    },
    handleGlobalFilterRemove (filter) {
      if (filter.source !== 'global') {
        const index = this
              .graphFilters
              .findIndex(f => f.filter === filter.filter && f.source === filter.source)
        if (index > -1) {
          this.graphFilters.splice(index, 1)
        }
      }
    },
    reset () {
      this.filters.filter(f => this.isVisible(f)).forEach((f, i) => {
        if (this.$refs.filter) {
          this.$refs.filter[i].reset()
        }
        Vue.delete(this.filters[i], 'active')
        this.filters.forEach(f => Vue.delete(f, 'active'))
        this.graphFilters = this.graphFilters.filter(f => f.filter !== filter.filter)
      })
      EventBus.$emit('reset')
    },
    load () {
      this.$store.dispatch('customGraph/loadDimensions')
      return api.get('custom-graph-global/widget/' + this.widget.id).then(({data}) => {
        return Promise
          .all(data.filter.map(
            f => this.$store.dispatch('customGraph/loadDimension', f.value)
          ).concat(data.filter.map(f => this.$store.dispatch('customGraph/loadValues', f.value))))
          .then(() => {
            this.filters.splice(0, this.filters.length, ...data.filter.map(f => {
              const dimension = this.$store.getters['customGraph/dimension'](f.value)
              if (typeof dimension === 'undefined') {
                return null
              }
              return {
                value: this.$store.getters['customGraph/dimension'](f.value),
                options: {
                  type: f.type
                },
                defaultValues: f.defaultValues ? f.defaultValues.map(d => dimension.values.find(
                  v => this.api.dimension.uniqueName(v) === d
                )) : [],
                state: f.state || 'editable',
                key: f.key || null,
                preventSave: true
              }
            }))
            if (this.filters.length) {
              this.setFilters()
            }
          })
      })
    },
    setFilters () {
      if (typeof this.$refs.filter === 'undefined') {
        setTimeout(() => this.setFilters(), 500)
      } else {
        this.$refs.filter.forEach((f, i) => {
          this.filters[i].preventSave = true
          if (this.filters[i].key) {
            const filters = this.$store.getters['customGraph/filter'](this.filters[i].key)
            if (filters) {
              f.setValues(filters, false).then(() => {
                this.filters[i].preventSave = false
              })              
              return
            }
          }
          f.setValues(this.filters[i].defaultValues, false).then(() => {
            this.filters[i].preventSave = false
          })
        })
      }
    },
    save () {
      return api.post('custom-graph-global/widget/' + this.widget.id, {
        filter: this.filters.map(f => {
          return {
            value: this.api.dimension.uniqueName(f.value),
            type: f.options.type,
            state: f.state || 'editable',
            key: f.key,
            defaultValues: f.defaultValues.filter(v => typeof v !== 'undefined').map(v => {
              return this.api.dimension.uniqueName(v)
            })
          }
        })
      }).then(this.cancel)
    },
    updateFilters (val) {
      const filter = this.filters.find(f => this.api.dimension.uniqueName(f.value) === val.dimension)
      if (typeof filter.active === 'undefined') {
        Vue.set(filter, 'active', [])
        Vue.set(filter, 'activeType', filter.options.type)
      }
      filter.active.splice(0, filter.active.length, ...val.values)
      if (!filter.preventSave) {
        this.$store.commit('customGraph/setFilter', {key: filter.key, values: filter.active})
      }
      EventBus.$emit('filter', [].concat(this.graphFilters, ...this.filters.map(
        f => typeof f.active === 'undefined' ? [] : f.active.map(a => {
          return {
            filter: a.uniqueName,
            filterName: a.name,
            data: null,
            source: 'global'
          }
        })
      )))
    },
    doSetMenu () {
      // only to avoid call with parameters on 'edit' event trigger
      this.setMenu()
    }
  },
  components: {
    MultipleCascader,
    ...filters
  }
}
</script>

<style scoped>
.filters {
    display: flex;
    flex-basis: 150px;
    flex-wrap: wrap;
}
.global-filters {
    margin-top: 10px;
}
.filters .el-form-item, .filters .merged-graph-filter {
    padding: 0 5px;
    margin-left: 0.4em;
    margin-right: 0.2em;
    margin-bottom: 0.4em;
}
</style>

<style>
.filters .el-select__tags > span {
    display: flex;
    flex-direction: column;
    width: 100%;
}
.filters .el-select__tags > span .el-tag {
    width: 100%;
    display: flex;
    align-items: center;
}
.filters .el-select__tags-text {
    max-width: 90%;
    min-width: 90%;
    overflow: hidden;
}
.filters i.el-tag__close {
    flex-shrink: 0;
}
</style>




// WEBPACK FOOTER //
// node_modules/macarons-graphs-front/src/GlobalFiltersWidget.vue